import { Bell, ChevronsUpDown, Loader2, Lock, MoveRight, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { StepIcon, type StepIconTone, stepLabel } from '@/features/workflows/stepIcons';
import { cn } from '@/lib/utils';

import type { SessionDto } from '../schemas/sessionSchemas';
import { formatMobile } from '../sessionFieldFormat';
import {
  displayApplicantName,
  displayReferenceId,
  NAME_PLACEHOLDER_TABLE,
  toDisplayCase,
} from '../sessionReference';
import { OriginalStatusBadge } from './statusAxisBadges';
import { VerifiedStatusCell } from './VerifiedStatusCell';

// The sessions table, ported from the designer's SessionsTable. Same eleven columns,
// same colours, same sticky tinted header.
//
// The Action column follows the design: ONE icon per row, chosen by status — the bell
// reminder, the lock indicator, or the refresh. See RowAction for the precedence.
//
// One deliberate departure: sorting is not wired to the backend yet, so the sort chevrons
// the design puts on five headers are omitted rather than rendered as decoration that does
// nothing when clicked. `SortableHeader` below marks where they attach.
//
// Rows are of two kinds. Most are sessions; under the Not Started filter the list also
// holds INVITES that were emailed and never opened. They render identically — an unopened
// invite is a Not Started run to an admin — but cannot navigate, be selected, or resend
// through the session endpoint. Each of those is handled where it applies below.

const CELL = 'px-[clamp(10px,0.8vw,14px)] py-[clamp(10px,1.1vh,14px)] align-middle';

/**
 * The ONE grey for the row's placeholder marks: a timestamp that hasn't happened yet, the
 * Action column's dash, and the non-actionable Locked indicator.
 *
 * #B3B3B3 is the designer's neutral — the same value as `.sc-sessions-badge--na`, so a
 * muted mark and the NA pill read as the same kind of absence. These three previously
 * disagreed (#9CA3AF for two of them, #B3B3B3 for the other); a constant keeps them from
 * drifting apart again.
 *
 * NOT used for the User column's NA — that one is styled like a real name, see the cell.
 */
const MUTED = 'text-[#B3B3B3]';
const HEAD_CELL =
  'px-[clamp(10px,0.8vw,14px)] py-[clamp(9px,1vh,11px)] text-left font-normal text-[length:var(--text-16)] text-[#041A3A] whitespace-nowrap';

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const timeFmt = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

/**
 * Step outcome → icon tint, matching the designer's `.sc-step-icon--*` classes.
 *
 * `Pending` is neutral grey, with one exception handled by the caller: the step the
 * applicant is actually on turns blue. Which step that is depends on the run as a
 * whole, not on the step, so it cannot be decided from a status alone.
 */
const TONE_BY_STEP_STATUS: Record<string, StepIconTone> = {
  Passed: 'green',
  Failed: 'red',
  Review: 'orange',
  Pending: 'neutral',
};

/**
 * The workflow's steps as a left-to-right flow of tinted icons joined by arrows —
 * the design's at-a-glance summary of how far a run got and where it went wrong.
 */
/**
 * How many step icons fit on one line in the Workflow column.
 *
 * Six, matching the questionnaires list's Features column, so the two tables truncate the same way.
 * The column is capped at 280px and each icon plus its arrow is ~31px, which is where six lands.
 */
const VISIBLE_STEPS = 6;

function StepFlow({
  steps,
  originalStatus,
}: {
  steps: SessionDto['steps'];
  originalStatus: string;
}) {
  if (steps.length === 0) return null;

  // Only a run that is actually moving has a "current" step to highlight. A
  // NotStarted run has touched nothing, and Locked / Expired / finished runs are not
  // waiting on the applicant, so in those cases every pending step stays grey.
  const currentIndex =
    originalStatus === 'InProgress' ? steps.findIndex((s) => s.status === 'Pending') : -1;

  const shown = steps.slice(0, VISIBLE_STEPS);
  const hidden = steps.slice(VISIBLE_STEPS);

  return (
    // ONE LINE, never wrapping. This was `flex-wrap`, so a nine-step workflow spilled onto a second
    // row and made that session's row visibly taller than its neighbours — the table lost its rhythm
    // and the extra icons said nothing the "+N more" does not.
    <div className="flex max-w-[280px] items-center gap-1.5 whitespace-nowrap">
      {shown.map((step, i) => (
        <span
          // Keyed by position, not array index: a workflow can hold two steps of the
          // same type, and position is what actually identifies one.
          key={step.position}
          className="flex shrink-0 items-center gap-1.5"
        >
          <StepIcon
            stepType={step.stepType}
            tone={i === currentIndex ? 'blue' : (TONE_BY_STEP_STATUS[step.status] ?? 'neutral')}
          />
          {i < shown.length - 1 && <MoveRight size={13} className="shrink-0 text-[#666666]" />}
        </span>
      ))}

      {hidden.length > 0 && (
        <span
          className="shrink-0 text-[length:var(--text-12)] font-medium text-[#6B7280]"
          // Names what is hidden, so the badge is informative rather than only a count.
          title={hidden.map((step) => stepLabel(step.stepType)).join(', ')}
        >
          +{hidden.length} more
        </span>
      )}
    </div>
  );
}

/** Date over time, or a dash when the moment hasn't happened yet. */
function TimestampCell({ iso }: { iso: string | null }) {
  if (!iso) {
    return <span className={cn('text-[length:var(--text-14)]', MUTED)}>—</span>;
  }
  const at = new Date(iso);
  return (
    <div className="flex flex-col">
      <span className="text-[length:var(--text-14)] text-[#666666]">{dateFmt.format(at)}</span>
      <span className="text-[length:var(--text-12)] text-[#666666]">
        {timeFmt.format(at).toLowerCase()}
      </span>
    </div>
  );
}

/**
 * The design's progress bar: track plus fill, and no percentage label.
 *
 * Green at 100%, blue below it — the designer's `--full` / `--partial` pairing. (Our
 * previous bar had these the other way round, so a finished run read as blue.)
 */
function ProgressBar({ percent }: { percent: number }) {
  return (
    <div className="flex w-16 items-center" title={`${percent}% complete`}>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#E2E8F0]">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            percent === 100 ? 'bg-[#00C232]' : 'bg-[#0048FF]',
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/**
 * A header that will carry sorting. The chevron is rendered only once a column is
 * actually sortable server-side, so the affordance never lies about what it does.
 */
function SortableHeader({ label, sortable = false }: { label: string; sortable?: boolean }) {
  return (
    <th className={HEAD_CELL}>
      <div className="flex items-center gap-1">
        {label}
        {sortable && <ChevronsUpDown size={14} className="shrink-0 opacity-60" />}
      </div>
    </th>
  );
}

export function SessionsTable({
  sessions,
  isLoading,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onResend,
  resendingId,
}: {
  sessions: SessionDto[];
  isLoading: boolean;
  selectedIds: string[];
  onToggleRow: (id: string) => void;
  onToggleAll: (ids: string[]) => void;
  onResend: (session: SessionDto) => void;
  /** Id of the row whose reminder is in flight, so only that button spins. */
  resendingId: string | null;
}) {
  const navigate = useNavigate();

  // Select-all covers only the rows that CAN be acted on. Invite rows have no session
  // behind them, so including them would tick boxes the delete endpoint must then reject —
  // and would leave the header box permanently indeterminate on a page that holds any.
  const allIds = sessions.filter((s) => s.rowKind !== 'Invite').map((s) => s.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = allIds.some((id) => selectedIds.includes(id));

  return (
    <div className="min-h-0 w-full flex-1 overflow-auto rounded-xl bg-white ring-1 ring-[#D0D4E4]">
      <table className="w-full min-w-[1080px] border-collapse">
        <thead className="sticky top-0 z-10 bg-[#F8FAFE] text-left text-[#041A3A]">
          <tr>
            <th className={cn(HEAD_CELL, 'w-10 text-center')}>
              <input
                type="checkbox"
                aria-label="Select all sessions on this page"
                className="h-4 w-4 cursor-pointer rounded align-middle accent-[#0048FF]"
                checked={allSelected}
                // Indeterminate is a DOM property, not an attribute — React has no
                // prop for it, so it is set through the ref.
                ref={(el) => {
                  if (el) el.indeterminate = !allSelected && someSelected;
                }}
                onChange={() => onToggleAll(allIds)}
              />
            </th>
            {/* "Session Id" is the design's heading and it stays, even though the cell
                now shows the mobile number. The design's own mock has phone numbers and
                email addresses under this heading — it means "how this run is
                identified", not "the id column". */}
            <SortableHeader label="Session Id" />
            <SortableHeader label="User" />
            <SortableHeader label="Workflow" />
            <SortableHeader label="Created" />
            <SortableHeader label="Submitted" />
            <SortableHeader label="Verified" />
            <SortableHeader label="Original Status" />
            <SortableHeader label="Verified Status" />
            <SortableHeader label="Progress" />
            <th className={cn(HEAD_CELL, 'text-center')}>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={11} className="px-3.5 py-10 text-center text-[#666666]">
                Loading…
              </td>
            </tr>
          ) : sessions.length === 0 ? (
            <tr>
              <td colSpan={11} className="px-3.5 py-10 text-center text-[#666666]">
                No sessions yet. Click <span className="font-medium">Start Session</span> to begin
                one.
              </td>
            </tr>
          ) : (
            sessions.map((session) => {
              const selected = selectedIds.includes(session.id);
              // An emailed-but-never-opened invite has no session behind it, so the row
              // cannot navigate and cannot be selected for deletion — the delete endpoint
              // takes session ids. Everything else about the row is identical, which is
              // the point: to an admin this IS a Not Started run.
              const isInvite = session.rowKind === 'Invite';
              const open = () => navigate(`/sessions/${session.id}`);

              /**
               * The row navigates only for clicks that physically happened INSIDE it.
               *
               * The verdict cell opens its dropdown and its dialogs through a React portal,
               * so they render under `document.body` — but React's synthetic events bubble
               * along the REACT tree, not the DOM tree, so a click on a radio inside the
               * Approve dialog still arrived here and navigated to the session view behind
               * it. `contains` is the reliable test: the portalled content is not a DOM
               * descendant of this row, so it is ignored.
               *
               * Guarding here rather than calling stopPropagation in each portalled child
               * fixes it once, for every portal this row will ever contain, instead of
               * relying on each new one to remember.
               */
              const openFromRow = (e: React.MouseEvent<HTMLTableRowElement>) => {
                if (!e.currentTarget.contains(e.target as Node)) return;
                open();
              };
              return (
                <tr
                  key={session.id}
                  tabIndex={isInvite ? undefined : 0}
                  onClick={isInvite ? undefined : openFromRow}
                  // The row acts as a link, so it answers Enter/Space too — a
                  // mouse-only row is unreachable by keyboard.
                  onKeyDown={
                    isInvite
                      ? undefined
                      : (e) => {
                          // Same containment guard as the click: Enter on a button inside a
                          // portalled dialog must not also open the session behind it.
                          if (!e.currentTarget.contains(e.target as Node)) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            open();
                          }
                        }
                  }
                  className={cn(
                    'border-t border-[#ECEEF3] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0048FF]',
                    !isInvite && 'cursor-pointer',
                    selected ? 'bg-[#F0F5FF]/50 hover:bg-[#EBF2FF]/70' : 'hover:bg-[#F9FAFB]',
                  )}
                >
                  {/* Every control below stops propagation on ITSELF rather than on
                      its cell: the cells are plain <td>s, and hanging a click handler
                      on one would make a non-interactive element behave as if it were
                      interactive. */}
                  <td className={cn(CELL, 'text-center')}>
                    <input
                      type="checkbox"
                      aria-label={
                        isInvite
                          ? 'Invites cannot be deleted from here'
                          : `Select session ${session.referenceId}`
                      }
                      // Disabled rather than hidden: an empty cell reads as a rendering
                      // gap, while a disabled box shows the row exists but is not
                      // selectable. Select-all also skips these (see allSelectableIds).
                      disabled={isInvite}
                      title={isInvite ? 'An unopened invite has no session to delete' : undefined}
                      className="h-4 w-4 cursor-pointer rounded align-middle accent-[#0048FF] disabled:cursor-not-allowed disabled:opacity-40"
                      checked={selected}
                      onChange={() => onToggleRow(session.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>

                  {/* The OTP-verified mobile number — the thing that actually identifies a
                      run to a human. Falls back to the session's Reference ID for
                      admin-started runs (no OTP, so no number), whether that is a typed
                      Session Id or the one minted at creation. The internal session id
                      stays on the cell's title for support lookups. */}
                  <td
                    className={cn(
                      CELL,
                      'max-w-[180px] truncate text-[length:var(--text-14)] text-[#041A3A]',
                    )}
                    title={session.id}
                  >
                    {formatMobile(session.applicantMobile) ??
                      displayReferenceId(session.referenceId) ??
                      'NA'}
                  </td>

                  {/* User — the verified name via `displayApplicantName`, the one helper
                      shared with the detail sidebar, tabs and Overview so they agree. This
                      column uses the design's table spelling ("NA", not "N/A").
                      It is styled EXACTLY like a real name — same colour, same weight.
                      The design's own mock renders "NA" in this column identically to
                      "Virag Kubadiya" beside it, and it reads better: this column is one
                      short piece of text per row, so dimming some of it just makes the
                      column look patchy. The dashes elsewhere in the row stay muted
                      because those cells sit among dense content that a full-strength
                      dash would compete with. */}
                  <td
                    className={cn(
                      CELL,
                      'whitespace-nowrap text-[length:var(--text-14)] font-medium text-[#041A3A]',
                    )}
                  >
                    {displayApplicantName(session.applicantFullName, NAME_PLACEHOLDER_TABLE)}
                  </td>

                  {/* Workflow name over its step flow. */}
                  <td className={CELL}>
                    <div className="flex flex-col gap-1.5 py-0.5">
                      <span className="text-[length:var(--text-14)] font-semibold text-[#041A3A]">
                        {session.workflowName}
                      </span>
                      <StepFlow steps={session.steps} originalStatus={session.originalStatus} />
                    </div>
                  </td>

                  <td className={cn(CELL, 'whitespace-nowrap')}>
                    <TimestampCell iso={session.createdAt} />
                  </td>
                  <td className={cn(CELL, 'whitespace-nowrap')}>
                    <TimestampCell iso={session.submittedAt} />
                  </td>
                  <td className={cn(CELL, 'whitespace-nowrap')}>
                    <TimestampCell iso={session.verifiedAt} />
                  </td>

                  {/* The system's verdict — read-only by definition. */}
                  <td className={cn(CELL, 'whitespace-nowrap')}>
                    <OriginalStatusBadge status={session.originalStatus || session.status} />
                  </td>

                  {/* The admin's verdict — the one status a human sets. Given the SAME
                      resolved original status as the badge beside it, since that is what
                      decides which verdicts this row may be given. */}
                  <td className={cn(CELL, 'whitespace-nowrap')}>
                    <VerifiedStatusCell
                      session={{
                        id: session.id,
                        originalStatus: session.originalStatus || session.status,
                        verifiedStatus: session.verifiedStatus,
                        label:
                          formatMobile(session.applicantMobile) ??
                          displayReferenceId(session.referenceId) ??
                          undefined,
                        // Case-normalised like the User column beside it, so the dialog
                        // and the row cannot spell the same person differently.
                        applicantName: session.applicantFullName
                          ? toDisplayCase(session.applicantFullName)
                          : null,
                        email: session.inviteEmail,
                        // The list carries only type + outcome per step, which is exactly
                        // what the resubmission dialog's check list needs.
                        steps: session.steps,
                      }}
                    />
                  </td>

                  <td className={CELL}>
                    <ProgressBar percent={session.completionPercent} />
                  </td>

                  {/* ONE action per row, chosen by status — the designer's layout.
                      Two icons were removed rather than restyled:
                        • the eye, because the whole row already opens the session, so
                          it was a second control for the same thing;
                        • the bin, because deletion lives in the toolbar above, which
                          works on a selection and asks for confirmation. A per-row bin
                          beside a status dropdown is a destructive action one pixel
                          from a routine one.
                      What remains is whichever action the row actually needs; see
                      rowAction() for the precedence. */}
                  <td className={cn(CELL, 'text-center')}>
                    <div className="flex items-center justify-center">
                      <RowAction
                        session={session}
                        isResending={resendingId === session.id}
                        onResend={() => onResend(session)}
                        onNavigate={navigate}
                      />
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The single action a row offers, resolved from its status.
 *
 * Precedence is deliberate — the most blocking thing first:
 *   1. Locked   — attempts exhausted. Nothing to do until the lock expires, so the
 *                 icon is a disabled indicator rather than a control. Showing a
 *                 working button here would invite a click that the API refuses.
 *   2. InReview — the run is waiting on an ADMIN. Reviewing is the action.
 *   3. Waiting on the applicant (NotStarted / InProgress) — resend the link when
 *                 there is an address to send it to, otherwise open the fill page so
 *                 an internally-run session can be continued.
 *   4. Anything settled (Approved / Rejected / Expired) — no action. Renders a dash,
 *                 not an empty cell, so the column reads as "nothing to do" rather
 *                 than as something that failed to load.
 *
 * An INVITE row short-circuits all of that: the only thing that can be done with an
 * unopened invite is send it again, and that goes to the invite endpoint. It is handled
 * first so it cannot fall into the session branches below, which would call the session
 * resend with an invite id.
 */
function RowAction({
  session,
  isResending,
  onResend,
  onNavigate,
}: {
  session: SessionDto;
  isResending: boolean;
  onResend: () => void;
  onNavigate: (to: string) => void;
}) {
  const status = session.originalStatus;

  if (session.rowKind === 'Invite') {
    return (
      <ActionButton
        label={`Send the invite to ${session.inviteEmail ?? 'this recipient'} again`}
        disabled={isResending}
        onClick={onResend}
      >
        {isResending ? (
          <Loader2 size={15} className="animate-spin text-[#666666]" />
        ) : (
          <Bell size={15} className="text-[#0048FF]" />
        )}
      </ActionButton>
    );
  }

  if (status === 'Locked') {
    return (
      <span
        title="Locked — too many attempts. Unlocks automatically."
        className={cn('flex h-7 w-7 items-center justify-center', MUTED)}
      >
        <Lock size={15} />
      </span>
    );
  }

  if (status === 'InReview') {
    return (
      <ActionButton
        label="Review this session"
        onClick={() => onNavigate(`/sessions/${session.id}/review`)}
      >
        <RotateCcw size={15} className="text-[#00A92C]" />
      </ActionButton>
    );
  }

  if (status === 'NotStarted' || status === 'InProgress') {
    if (session.inviteEmail) {
      return (
        <ActionButton
          label={`Resend link to ${session.inviteEmail}`}
          disabled={isResending}
          onClick={onResend}
        >
          {isResending ? (
            <Loader2 size={15} className="animate-spin text-[#666666]" />
          ) : (
            <Bell size={15} className="text-[#0048FF]" />
          )}
        </ActionButton>
      );
    }
    return (
      <ActionButton
        label="Continue this session"
        onClick={() => onNavigate(`/sessions/${session.id}/fill`)}
      >
        <RotateCcw size={15} className="text-[#0048FF]" />
      </ActionButton>
    );
  }

  return <span className={cn('text-[length:var(--text-14)]', MUTED)}>—</span>;
}

/** The design's square icon button: 28px, rounded, grey hover. */
function ActionButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // Each action is its own destination — none of them should also trigger the
        // row's navigate.
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-50"
    >
      {children}
    </button>
  );
}
